import { useContext, useState } from "react";
import { ensure, FormDynamicTextFieldInput } from "sprint-common-web";

import { CreateFileRecordResponse } from "../../../../../common/api/master-api/file-records/create/CereateFileRecordResponse";
import { CreateAudioFileRecordRequest } from "../../../../../common/api/master-api/file-records/create/CreateAudioFileRecordRequest";
import { CreateSubtitleFileRecordRequest } from "../../../../../common/api/master-api/file-records/create/CreateSubtitleFileRecordRequest";
import {
    CreateVideoFileRecordRequest,
    CreateVideoFileRecordSubtitleRequest
} from "../../../../../common/api/master-api/file-records/create/CreateVideoFileRecordRequest";
import { useFileRecordApi } from "../../../../../common/api/master-api/file-records/useFileRecordApi";
import { useNotesActions } from "../../../../../common/components/notes/hooks/useNotesActions";
import { LanguagesDataContext } from "../../../../../common/data/lookup-data-providers/LanguagesDataProvider";
import { OriginatorsDataContext } from "../../../../../common/data/lookup-data-providers/OriginatorsDataProvider";
import { EmbeddedSubtitleType } from "../../../../../common/types/EmbeddedSubtitleType";
import { NoteSubjectType } from "../../../../../common/types/NoteSubjectType";
import { StorageType } from "../../../../../common/types/StorageType";
import { useMapToSubmissionsHistoryContent } from "../../../../common/content/buttons/lookups/lookup-history/mapToSubmissionsHistoryContent";
import { useAssociateFormContentReferenceMapper } from "../../../../common/content/hooks/useAssociateFormContentReferenceMapper";
import { useAssociateHistory } from "../../../../quality-control/useAssociateHistory";
import { FileRecordMovieVersionItem } from "../../../common/types/FileRecordMovieItem";
import { FileRecordEpisodeVersionItem } from "../../../common/types/FileRecordTvSeriesItem";
import { CreateFileRecordFormInputs } from "../CreateFileRecordFormInputs";

export type SelectedContentVersion = FileRecordMovieVersionItem | FileRecordEpisodeVersionItem;

export const useCreateFileRecord = () => {
    const [isUpdatePending, setIsUpdatedPending] = useState(false);
    const { originators } = useContext(OriginatorsDataContext);
    const { embeddedSubtitleLanguages: embeddedSubtitles } = useContext(LanguagesDataContext);

    const { mapFormStateToContentReference } = useAssociateFormContentReferenceMapper();

    const { createVideoFileRecord, createAudioFileRecord, createSubtitleFileRecord } = useFileRecordApi();
    const { createNotes } = useNotesActions();

    const { mapToSubmissionsHistoryContent } = useMapToSubmissionsHistoryContent();

    const associateHistory = useAssociateHistory();

    const createFileRecordNotes = async (fileRecordId: string, notes: FormDynamicTextFieldInput[]): Promise<void> => {
        const items = notes
            .filter((note) => note && note.value)
            .map((note) => ({ subjectId: fileRecordId, content: note.value, subjectType: NoteSubjectType.FileRecord }));

        if (items.length === 0) {
            return;
        }

        setIsUpdatedPending(true);

        await createNotes(items).finally(() => setIsUpdatedPending(false));
    };

    const createAudio = async (formInputs: CreateFileRecordFormInputs): Promise<CreateFileRecordResponse> => {
        setIsUpdatedPending(true);
        const originatorReference = originators.find((originator) => originator.id === formInputs.originator);
        ensure(originatorReference);

        const storageType = Number(formInputs.storageType);
        const request: CreateAudioFileRecordRequest = {
            deliveryInfo: {
                arrivalDate: formInputs.arrivalDate.toISOString(),
                fileName: formInputs.fileName,
                originator: originatorReference,
                path: storageType === StorageType.OnPrem ? formInputs.path : undefined,
                reference: formInputs.reference
            },
            content: await mapFormStateToContentReference(formInputs),
            metadata: {
                audioFormat: formInputs.audioFormat,
                audioBitRate: formInputs.audioBitrate ? Number(formInputs.audioBitrate) : undefined,
                sampleRate: formInputs.audioSampleRate ? Number(formInputs.audioSampleRate) : undefined,
                channelsCount: formInputs.audioChannels ? Number(formInputs.audioChannels) : undefined,
                duration: formInputs.audioDuration
            },
            soundtrackLanguageId: formInputs.audioSoundtrack ? formInputs.audioSoundtrack : undefined,
            storageType: storageType,
            isNotificationApproved: formInputs.isNotificationApproved || false
        };

        return createAudioFileRecord(request).finally(() => setIsUpdatedPending(false));
    };

    const createSubtitle = async (formInputs: CreateFileRecordFormInputs): Promise<CreateFileRecordResponse> => {
        setIsUpdatedPending(true);
        const originatorReference = originators.find((originator) => originator.id === formInputs.originator);
        ensure(originatorReference);

        const storageType = Number(formInputs.storageType);
        const request: CreateSubtitleFileRecordRequest = {
            deliveryInfo: {
                arrivalDate: formInputs.arrivalDate.toISOString(),
                fileName: formInputs.fileName,
                originator: originatorReference,
                path: storageType === StorageType.OnPrem ? formInputs.path : undefined,
                reference: formInputs.reference
            },
            content: await mapFormStateToContentReference(formInputs),
            subtitleLanguageId: formInputs.subtitleSubtitles?.length ? formInputs.subtitleSubtitles : undefined,
            storageType: Number(formInputs.storageType),
            isNotificationApproved: formInputs.isNotificationApproved || false
        };

        return createSubtitleFileRecord(request).finally(() => setIsUpdatedPending(false));
    };

    const createVideo = async (formInputs: CreateFileRecordFormInputs) => {
        setIsUpdatedPending(true);

        const originatorReference = originators.find((originator) => originator.id === formInputs.originator);
        ensure(originatorReference);

        const storageType = Number(formInputs.storageType);
        const request: CreateVideoFileRecordRequest = {
            soundtrackLanguageIds: formInputs.videoSoundtracks,
            subtitle: getEmbeddedSubtitle(formInputs.videoSubtitle),
            metadata: {
                aspectRatioId: formInputs.videoAspectRatio,
                aspectTypeId: formInputs.videoAspectType,
                videoBitRate: formInputs.videoBitrate ? Number(formInputs.videoBitrate) : undefined,
                duration: formInputs.videoDuration,
                framerate: formInputs.videoFramerate,
                resolution: formInputs.videoResolution,
                standardId: formInputs.videoStandard,
                encoding: formInputs.videoEncoding
            },
            deliveryInfo: {
                arrivalDate: formInputs.arrivalDate.toISOString(),
                fileName: formInputs.fileName,
                originator: originatorReference,
                path: storageType === StorageType.OnPrem ? formInputs.path : undefined,
                reference: formInputs.reference
            },
            content: await mapFormStateToContentReference(formInputs),
            storageType: Number(formInputs.storageType),
            isNotificationApproved: formInputs.isNotificationApproved || false
        };

        return createVideoFileRecord(request).finally(() => setIsUpdatedPending(false));
    };

    const getEmbeddedSubtitle = (subtitleLanguageId?: string): CreateVideoFileRecordSubtitleRequest | undefined => {
        if (!subtitleLanguageId) return undefined;

        const embeddedSub = embeddedSubtitles.find((s) => s.id === subtitleLanguageId);
        ensure(embeddedSub);

        if (embeddedSub.type === EmbeddedSubtitleType.CleanVideo) {
            return { type: EmbeddedSubtitleType.CleanVideo };
        }

        return { languageId: subtitleLanguageId, type: EmbeddedSubtitleType.Subtitle };
    };

    const saveToHistory = async (formInputs: CreateFileRecordFormInputs) => {
        const historyContent = await mapToSubmissionsHistoryContent(formInputs);

        if (historyContent) {
            associateHistory.save(historyContent);
        }
    };

    return {
        createVideo,
        createAudio,
        createSubtitle,
        createFileRecordNotes,
        isUpdatePending,
        saveToHistory
    };
};
